export interface UsageInfo {
  usedCount: number;
  paidCount: number;
  freeCount: number;
  remaining: number;
  hasPaid: boolean;
}

const STORAGE_KEY = 'iching_usage_data';
const PAYWALL_SHOWN_KEY = 'iching_paywall_shown';
const FREE_LIMIT = 5;
const PAID_PACKAGE = 30;

interface UsageData {
  used: number;
  paidPackages: number;
}

function getUsageData(): UsageData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { used: 0, paidPackages: 0 };
    const parsed = JSON.parse(raw);
    return {
      used: typeof parsed.used === 'number' ? parsed.used : 0,
      paidPackages: typeof parsed.paidPackages === 'number' ? parsed.paidPackages : 0,
    };
  } catch {
    return { used: 0, paidPackages: 0 };
  }
}

function saveUsageData(data: UsageData): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function getUsageInfo(): UsageInfo {
  const data = getUsageData();
  const freeCount = FREE_LIMIT;
  const paidCount = data.paidPackages * PAID_PACKAGE;
  const totalAllowed = freeCount + paidCount;
  const remaining = Math.max(0, totalAllowed - data.used);

  return {
    usedCount: data.used,
    paidCount: paidCount,
    freeCount: freeCount,
    remaining,
    hasPaid: data.paidPackages > 0,
  };
}

/**
 * 使用1次。返回 true 表示成功扣除，false 表示次数已用完
 */
export function consumeUsage(): boolean {
  const data = getUsageData();
  const totalAllowed = FREE_LIMIT + data.paidPackages * PAID_PACKAGE;

  if (data.used >= totalAllowed) {
    return false;
  }

  data.used += 1;
  saveUsageData(data);
  return true;
}

/**
 * 购买1个付费包（30次）
 */
export function addPaidPackage(): void {
  const data = getUsageData();
  data.paidPackages += 1;
  saveUsageData(data);
}

/**
 * 记录支付弹窗已被触发弹出过
 */
export function markPaywallShown(): void {
  localStorage.setItem(PAYWALL_SHOWN_KEY, '1');
}

/**
 * 检查支付弹窗是否已被触发弹出过
 */
export function hasPaywallBeenShown(): boolean {
  try {
    return localStorage.getItem(PAYWALL_SHOWN_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * 清除支付弹窗标记（购买成功后重置，下次次数用完可以再次弹出）
 */
export function clearPaywallShown(): void {
  localStorage.removeItem(PAYWALL_SHOWN_KEY);
}

/**
 * 获取剩余次数描述文本
 */
export function getRemainingText(): string {
  const info = getUsageInfo();
  if (info.remaining <= 0) {
    return '次数已用完';
  }
  if (info.remaining <= 3) {
    return `剩余 ${info.remaining} 次`;
  }
  return `剩余 ${info.remaining} 次`;
}

/**
 * 重置次数（仅测试用）
 */
export function resetUsage(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(PAYWALL_SHOWN_KEY);
}